import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    videoLength: '',
    quantity: '',
    budget: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ 
        type: 'success', 
        message: 'Message sent successfully! We\'ll get back to you soon.' 
      });
      setFormData({
        name: '',
        email: '',
        videoLength: '',
        quantity: '',
        budget: '',
        message: ''
      });
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Something went wrong. Please try again or email us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full bg-black/50 border-0 border-b-2 border-neutral-700 px-4 py-4 text-white font-manrope placeholder-neutral-500 focus:outline-none focus:border-neon-red transition-colors duration-300";

  return (
    <section 
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-void-black relative"
    >
      {/* Section Divider */}
      <div className="section-divider w-full absolute top-0" />
      
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1702302014150-071425a164ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjByb2NrJTIwY29uY2VydCUyMGNyb3dkJTIwcmVkJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMjQ0NTkxfDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4">
            Start Your Project
          </p>
          <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight uppercase text-[#EDEDED]">
            Get In Touch
          </h2>
          <p className="font-manrope text-neutral-400 mt-6 max-w-xl mx-auto">
            Ready to bring your music to life? Fill out the form below and let's create something epic together.
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.form 
          data-testid="contact-form"
          onSubmit={handleSubmit}
          className="glass p-8 md:p-12 rounded-lg space-y-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Name & Email Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-manrope text-xs tracking-widest uppercase text-neutral-400 mb-2 block">
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                data-testid="contact-name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your name"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="font-manrope text-xs tracking-widest uppercase text-neutral-400 mb-2 block">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                data-testid="contact-email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                className={inputClasses}
              />
            </div>
          </div>

          {/* Video Length & Quantity Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-manrope text-xs tracking-widest uppercase text-neutral-400 mb-2 block">
                Video Length
              </label>
              <Select 
                value={formData.videoLength} 
                onValueChange={(value) => handleSelectChange('videoLength', value)}
              >
                <SelectTrigger 
                  data-testid="contact-video-length"
                  className="w-full bg-black/50 border-0 border-b-2 border-neutral-700 rounded-none h-14 text-white font-manrope focus:border-neon-red"
                >
                  <SelectValue placeholder="Select video length" />
                </SelectTrigger>
                <SelectContent className="bg-void-gray border border-neutral-700">
                  <SelectItem value="30sec">30 Seconds (£5)</SelectItem>
                  <SelectItem value="1min">1 Minute</SelectItem>
                  <SelectItem value="2-3min">2-3 Minutes</SelectItem>
                  <SelectItem value="5min">Up to 5 Minutes (£10)</SelectItem>
                  <SelectItem value="custom">Custom Length</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-manrope text-xs tracking-widest uppercase text-neutral-400 mb-2 block">
                Quantity
              </label>
              <Select 
                value={formData.quantity} 
                onValueChange={(value) => handleSelectChange('quantity', value)}
              >
                <SelectTrigger 
                  data-testid="contact-quantity"
                  className="w-full bg-black/50 border-0 border-b-2 border-neutral-700 rounded-none h-14 text-white font-manrope focus:border-neon-red"
                >
                  <SelectValue placeholder="How many videos?" />
                </SelectTrigger>
                <SelectContent className="bg-void-gray border border-neutral-700">
                  <SelectItem value="1">1 Video</SelectItem>
                  <SelectItem value="2-4">2-4 Videos</SelectItem>
                  <SelectItem value="5">5 Videos (Bulk Discount)</SelectItem>
                  <SelectItem value="10">10 Videos (Best Value)</SelectItem>
                  <SelectItem value="10+">10+ Videos (Custom Quote)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="font-manrope text-xs tracking-widest uppercase text-neutral-400 mb-2 block">
              Budget Range
            </label>
            <Select 
              value={formData.budget} 
              onValueChange={(value) => handleSelectChange('budget', value)}
            >
              <SelectTrigger 
                data-testid="contact-budget"
                className="w-full bg-black/50 border-0 border-b-2 border-neutral-700 rounded-none h-14 text-white font-manrope focus:border-neon-red"
              >
                <SelectValue placeholder="Select your budget" />
              </SelectTrigger>
              <SelectContent className="bg-void-gray border border-neutral-700">
                <SelectItem value="under-50">Under £50</SelectItem>
                <SelectItem value="50-100">£50 - £100</SelectItem>
                <SelectItem value="100-250">£100 - £250</SelectItem>
                <SelectItem value="250-500">£250 - £500</SelectItem>
                <SelectItem value="500+">£500+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div>
            <label className="font-manrope text-xs tracking-widest uppercase text-neutral-400 mb-2 block">
              Project Details *
            </label>
            <textarea
              name="message"
              data-testid="contact-message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              placeholder="Tell us about your project, music style, and any specific ideas you have..."
              className={`${inputClasses} resize-none`}
            />
          </div>

          {/* Status Message */}
          {status.message && (
            <motion.div 
              className={`flex items-center gap-3 p-4 rounded-lg ${
                status.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {status.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="font-manrope text-sm">{status.message}</span>
            </motion.div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={isSubmitting}
            className="w-full btn-primary py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed neon-glow-hover"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Message
              </>
            )}
          </button>

          {/* Direct Email */}
          <p className="text-center font-manrope text-neutral-500 text-sm">
            Or email us directly at{' '}
            <a 
              href="mailto:hypnoticvocals@gmail.com" 
              className="text-neon-red hover:underline"
            >
              hypnoticvocals@gmail.com
            </a>
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
